import { useContext, useEffect, useState, useRef } from "react";
import ThumbnailContext from "./ThumbnailContext";
import { THUMBNAIL_BATCH_SIZE, THUMBNAIL_FETCH_SIZE, PREFETCH_SERIES_DELAY_MS } from "../../constants/Constants";
import studyviewerContext from "./StudyviewerContext";
import { prefetchData, resetThumbnailPreloadState, stopAllPrefetchData } from "../../pages/StudyViewer/helpers/CornerstoneThumbnailService";
import { firstImageSeriesIndex, isNonImageSeries } from "../../pages/StudyViewer/helpers/seriesModality";
import { isMobile } from "react-device-detect";

const ThumbnailState = (props) => {

  const {nonFrontalImages, stackListSortedByImages, studyData, priorStudyData, isPriorsStudy, prefetchCount, hpNotFound, prefetchPaused} = useContext(studyviewerContext);

  // Preload pause/resume state (SharedViewer Basic<->Advance switch).
  // pausedRef mirrors prefetchPaused so the running async loop reads the latest
  // value without a stale closure. resumeIndexRef marks how far into
  // stackListSortedByImages the loop got, so a resume continues instead of
  // restarting from 0. prefetchRunningRef guards against two loops running at once.
  const pausedRef = useRef(false);
  const resumeIndexRef = useRef(0);
  const prefetchRunningRef = useRef(false);

  const [impressionHoveredStudyId, setImpressionHoveredStudyId] = useState(0);
  const [seriesHovered, setSeriesHovered] = useState(false);
  const [impressionHovered, setImpressionHovered] = useState(false);
  const [activeStudyId, setActiveStudyId] = useState("");
  const [activeModality, setActiveModality] = useState("");
  const [activeThumbnailIndex, setActiveThumbnailIndex] = useState(0);
  const [activeMPRThumbnailIndex, setActiveMPRThumbnailIndex] = useState(0);
  const [activeId, setActiveId] = useState("");
  const [activeColor, setActiveColor] = useState("");
  const [thumbnailElements, setThumbnailElements] = useState([]);
  const [lateralThumbnailIndex, setLateralThumbnailIndex] = useState(null);
  const [stackScrollActiveThumbnailIndex, setStackScrollActiveThumbnailIndex] =
    useState(-1);
  let tempThumbnailElements = [];
  const [thumbnailViewports, setThumbnailViewports] = useState([
    {
      element: "sampleElement",
      thumbnailIndex: "1",
    },
  ]);

  const [activeThumbnailData, setActiveThumbnailData] = useState({});
  const [thumbnailLoadingComplete, setThumbnailLoadingComplete] =
    useState(false);
  const [loadUptoIndex, setLoadUptoIndex] = useState(THUMBNAIL_BATCH_SIZE);

  const initialMap = new Map();
  for (let i = 0; i < 5; i++) {
    initialMap.set(i, false);
  }
  const [thumbnailLoadMap, setThumbnailLoadMap] = useState(initialMap);

  // Centralised thumbnail-batch gate. Thumbnails load in batches; the next batch
  // only opens once the current one is fully accounted for. PR/KO/SR series render
  // NO thumbnail (ThumbnailList returns null / SR early-returns), so their map
  // index never flips to true — if the per-thumbnail advance owns the gate, those
  // indices leave the batch permanently incomplete and ALL later thumbnails/images
  // stop loading (a study with derived PR/KO series opens blank). Owning the
  // advance here, re-evaluated on every map change and treating PR/KO/SR as done,
  // removes that race.
  useEffect(() => {
    const activeStudy = !isEmptyStudyData(studyData) ? studyData : priorStudyData?.[0];
    const totalStacks = activeStudy?.stacks?.length ?? 0;
    if (totalStacks === 0) return;

    const isNonRendering = (i) => isNonImageSeries(activeStudy.stacks?.[i]);

    // ThumbnailList remaps the FIRST renderable stack to index 0 (see the
    // effectiveIndex logic in ThumbnailList.jsx) so the viewport auto-load fires
    // even when PR/KO/SR series sort ahead of it. Consequence: when the first
    // renderable stack is NOT at real index 0, no Thumbnail ever mounts at that
    // real index — it renders (and reports updateThumbnailLoadMap) under index 0
    // instead. The gate must treat that orphaned real index as done, otherwise
    // it waits forever on a map entry nothing will ever set and every later
    // thumbnail stalls at 0% (exactly the PR-heavy-study bug this fixes).
    const firstRenderable = firstImageSeriesIndex(activeStudy.stacks);
    const isRemappedToZero = (i) =>
      typeof firstRenderable === "number" &&
      firstRenderable > 0 &&
      i === firstRenderable;

    const indexDone = (i) =>
      thumbnailLoadMap.get(i) === true || isNonRendering(i) || isRemappedToZero(i);

    // Open the next batch while the current gating batch is fully done.
    if (loadUptoIndex < totalStacks) {
      const batchStart = Math.max(0, loadUptoIndex - THUMBNAIL_BATCH_SIZE);
      let batchComplete = true;
      for (let i = batchStart; i < loadUptoIndex; i++) {
        if (!indexDone(i)) { batchComplete = false; break; }
      }
      if (batchComplete) {
        setThumbnailLoadMap((prev) => {
          const next = new Map(prev);
          for (let i = loadUptoIndex; i < loadUptoIndex + THUMBNAIL_BATCH_SIZE; i++) {
            if (!next.has(i)) next.set(i, false);
          }
          return next;
        });
        setLoadUptoIndex((prev) => prev + THUMBNAIL_BATCH_SIZE);
        return; // re-runs for the next batch after state updates
      }
    }

    // Everything gated in and every loadable thumbnail done → loading complete.
    // (The per-thumbnail "index === stackSize - 1" path never fires when the LAST
    // series is PR/KO/SR, so finalise here too.)
    if (loadUptoIndex >= totalStacks) {
      let allDone = true;
      for (let i = 0; i < totalStacks; i++) {
        if (!indexDone(i)) { allDone = false; break; }
      }
      if (allDone) setThumbnailLoadingComplete(true);
    }
  }, [thumbnailLoadMap, loadUptoIndex, studyData, priorStudyData]);

  // const [isPrefetchStarted, setIsPrefetchStarted] = useState(false);

  // Series the user paused from the thumbnail play/pause button are tracked by
  // thumbnail index, which the next study reuses. Forget them when the study
  // changes or the viewer closes.
  const preloadStudyId =
    studyData?.stacks?.[0]?.studyId ?? priorStudyData?.[0]?.stacks?.[0]?.studyId;
  useEffect(() => {
    resetThumbnailPreloadState();
    return () => resetThumbnailPreloadState();
  }, [preloadStudyId]);

  useEffect(()=> {
    if(thumbnailLoadingComplete && activeStudyId){
      const params = new URLSearchParams(window.location.search);
      const blitzSession = params.get("blitzSession");
      if(blitzSession){
        window.parent.onAllImagesLoaded(activeStudyId);
      }
    }
  }, [thumbnailLoadingComplete])

  const addThumbnailElements = (element) => {
    tempThumbnailElements.push(element);
    setThumbnailElements(tempThumbnailElements);
  };

  useEffect(() => {
    const fetchData = async () => {
      await startPrefetching();
    };

    // A new/changed stack list means a fresh study — restart the preload from 0.
    if (stackListSortedByImages.length > 0) {
      resumeIndexRef.current = 0;
      if (!pausedRef.current) {
        fetchData();
      }
    }
  }, [stackListSortedByImages]);

  // Pause/resume the preload on the SharedViewer Basic<->Advance switch.
  // Paused (Advance): abort in-flight downloads; the running loop bails at its
  // next checkpoint and records resumeIndexRef. Resumed (Basic): restart the loop,
  // which continues from resumeIndexRef.
  useEffect(() => {
    pausedRef.current = prefetchPaused;
    if (prefetchPaused) {
      stopAllPrefetchData();
    } else if (
      stackListSortedByImages.length > 0 &&
      resumeIndexRef.current < stackListSortedByImages.length
    ) {
      startPrefetching();
    }
  }, [prefetchPaused]);

  useEffect(() => {
    if(hpNotFound){
      setStackScrollActiveThumbnailIndex(0);
    }
  }, [hpNotFound]);

  const isEmptyStudyData = (data) => {
  if (!data) return true; // null or undefined
    return Object.values(data).every(
      (value) => Array.isArray(value) && value.length === 0
    );
  };
  
  // Resumable, pausable background preload. Walks stackListSortedByImages from
  // resumeIndexRef; on each iteration it checks pausedRef (flipped by the
  // Basic<->Advance switch) and bails out, recording its position so a later
  // resume continues from the same stack instead of restarting. prefetchRunningRef
  // ensures only one loop runs even if the trigger and the resume effect race.
  const startPrefetching = async () => {
    if (prefetchRunningRef.current) return;
    prefetchRunningRef.current = true;
    try {
      const params = new URLSearchParams(window.location.search);
      const blitzSession = params.get("blitzSession");
      const isBlitzSession = blitzSession && blitzSession === "true";
      let i = resumeIndexRef.current;
      for (; i < stackListSortedByImages.length; i++) {
        // Paused (switched to Advance viewer): abort anything in flight, remember
        // where we are, and stop. The resume effect restarts us from here.
        if (pausedRef.current) {
          resumeIndexRef.current = i;
          stopAllPrefetchData();
          return;
        }

        const item = stackListSortedByImages[i];
        const activeStudy = !isEmptyStudyData(studyData) ? studyData : priorStudyData?.[0];
        const stackAtIndex = activeStudy?.stacks?.[item.index];

        // SR/DOC/PR/KO series don't render an image thumbnail in the panel:
        //   - SR and DOC (Encapsulated PDF) have their own early-return path in
        //     Thumbnail.jsx — neither carries pixel data to prefetch
        //   - PR and KO are filtered out entirely by ThumbnailList (return null)
        // Either way, no Thumbnail at that index will ever call updateThumbnailLoadMap,
        // so we must mark the index loaded here. Otherwise the batch-completion check
        // never passes and loadUptoIndex stops advancing, leaving every later
        // thumbnail stuck on the spinner.
        // (and other series with no image to preload: ECG, SEG, RT…)
        if (isNonImageSeries(stackAtIndex)) {
          setThumbnailLoadMap((prevMap) => {
            const newMap = new Map(prevMap);
            newMap.set(item.index, true);
            return newMap;
          });
          continue;
        }

        await new Promise((resolve) => setTimeout(resolve, PREFETCH_SERIES_DELAY_MS));

        // Re-check after the delay — the user may have switched during the wait.
        if (pausedRef.current) {
          resumeIndexRef.current = i;
          stopAllPrefetchData();
          return;
        }

        if (activeStudy.stacks != undefined) {
          if (
            (!isMobile && (item.length <= prefetchCount || prefetchCount === -1)) ||
            (isMobile && item.length <= THUMBNAIL_FETCH_SIZE) ||
            isBlitzSession
          ) {
            // Download only: a series is decoded when it's shown (see prefetchData)
            await prefetchData(item.index, { bytesOnly: true });
          }
        }
      }
      // Completed the full list — park resumeIndexRef at the end so a stray
      // resume is a no-op.
      resumeIndexRef.current = stackListSortedByImages.length;
    } finally {
      prefetchRunningRef.current = false;
    }
  };
  


  const changeActiveThumbnailData = (study, series, modality) => {
    // console.log(
    //   "in changeActiveThumbnailData " + study + " " + series + " " + modality
    // );
    setActiveThumbnailData({ series, study, modality });
    // console.log(activeThumbnailData);
  };

  const changeActiveThumbnailIndex = (index) => {
    // console.log(index);
    setActiveThumbnailIndex(index != undefined ? Number(index) : -1);
  };

  const addthumbnailViewports = (element, thumbnailIndex) => {
    setThumbnailViewports(
      [...thumbnailViewports].push({
        element,
        thumbnailIndex,
      })
    );
  };

  const checkIfParentStudyThumbnailIsLoaded = (
    parentStudyId,
    viewportSeriesData,
    currentIndex
  ) => {
    let result = false;
    viewportSeriesData.map((data) => {
      if (
        data.study != "" &&
        data.study == parentStudyId &&
        data.thumbnailIndex == currentIndex
      ) {
        result = true;
      }
    });
    return result;
  };

  const checkForThumbnailReinference = (viewportSeriesData, forceReinferenceImageIds, index, thumbnailStack) => {

    let result = false;
    viewportSeriesData.map((data) => {
      if (
        data.study != "" && data.thumbnailIndex == index && forceReinferenceImageIds.length > 0
        && forceReinferenceImageIds.some(item => data.stack?.imageIds.includes(item))
      ) {
        result = true;
      }
    });

    if(nonFrontalImages.some(item => thumbnailStack?.imageIds.includes(item.imageUrl) && item.isSentForForceReinference)) {
      result = true;
    }

    return result;
  }

  return (
    <ThumbnailContext.Provider
      value={{
        activeThumbnailIndex,
        changeActiveThumbnailIndex,
        addthumbnailViewports,
        thumbnailViewports,
        setThumbnailViewports,
        changeActiveThumbnailData,
        activeThumbnailData,
        setActiveThumbnailData,
        addThumbnailElements,
        thumbnailElements,
        activeId,
        setActiveId,
        activeColor,
        setActiveColor,
        lateralThumbnailIndex,
        setLateralThumbnailIndex,
        activeMPRThumbnailIndex,
        setActiveMPRThumbnailIndex,
        activeModality,
        setActiveModality,
        setStackScrollActiveThumbnailIndex,
        stackScrollActiveThumbnailIndex,
        activeStudyId,
        setActiveStudyId,
        checkIfParentStudyThumbnailIsLoaded,
        impressionHoveredStudyId,
        setImpressionHoveredStudyId,
        seriesHovered,
        setSeriesHovered,
        impressionHovered,
        setImpressionHovered,
        thumbnailLoadingComplete,
        setThumbnailLoadingComplete,
        loadUptoIndex,
        setLoadUptoIndex,
        checkForThumbnailReinference,
        startPrefetching,
        thumbnailLoadMap, setThumbnailLoadMap
      }}
    >
      {props.children}
    </ThumbnailContext.Provider>
  );
};

export default ThumbnailState;
